import { useAppDispatch, useAppSelector } from 'appState/store'
import { resetCommonClaimsFilters, updateTriggerDownload } from 'features/claims/claimsSlice'
import { settingsServiceApiPaths, setUserPreference } from 'features/settings/settingsApi'
import { UserPreferencesKeysEnum } from 'features/settings/settingsTypes'
import styled, { css } from 'styled-components'

import { postbatchClaimsDownload } from 'features/autocheck/autoCheckApi'
import { useEffect, useState } from 'react'
import { FaDownload, FaFileDownload } from 'react-icons/fa'
import { Button, ObModal } from '@outbound-ai/ursa'
import { notificator } from 'utils/notificator/notificator'

export const ClaimsTableActiveFilters = () => {
  const [show, setShowModal] = useState(false)
  const { commonClaimsFilters } = useAppSelector((state) => state.claims)
  const [filterLength, setFilterLength] = useState(0)
  const { batchId } = useAppSelector((state) => state.claims.allClaimRouteUrl)

  const dispatch = useAppDispatch()

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const obGridParams = useAppSelector((state) => state.claims.claimsTable)

  useEffect(() => {
    setFilterLength(Object.keys(commonClaimsFilters)?.length)
  }, [commonClaimsFilters])

  if (!filterLength) return <></>

  const handleClearAll = async () => {
    !!filterLength && dispatch(resetCommonClaimsFilters())
    await setUserPreference(settingsServiceApiPaths.tenantUserPreferencesPut(), {
      key: UserPreferencesKeysEnum.claimsGridCommonFilters,
      value: JSON.stringify({}),
    })
    obGridParams.params.api.setFilterModel(null)
  }

  const confirmDownload = () => {
    dispatch(updateTriggerDownload(true))
    setShowModal(true)
  }

  const handelDownload = () => {
    const dataQuery = obGridParams.apiQuery
    postbatchClaimsDownload(dataQuery).then((res) => {
      if (res) {
        setShowModal(false)
        notificator.notify({
          type: 'info',
          heading: 'Preparing file for download ⏳',
          message: 'Once your file is ready to download it will be available in the Notifications menu',
        })
      }
    })
  }

  // variables
  const filtersCount = filterLength ? filterLength : 0
  const filtersTextPlural = filtersCount > 1 ? 's' : ''

  return (
    <Div data-testid="activeFiltersContainer" hasFilters={!!filterLength}>
      <div className="display user-select-none">
        <span data-testid="activeFiltersDisplay">
          <span>{batchId ? `+${filtersCount}` : filtersCount}</span> Filter{filtersTextPlural} Applied
        </span>
      </div>

      <Button
        variant="secondary"
        radius="rounded"
        contentType="text-icon"
        disabled={obGridParams.totalItems === 0}
        size="normal"
        onClick={confirmDownload}
      >
        Download <FaFileDownload />
      </Button>

      <Button
        data-testid="activeFiltersClearAll"
        variant="primary"
        radius="rounded"
        size="normal"
        onClick={handleClearAll}
      >
        Clear All
      </Button>
      <ObModal
        show={show}
        customSize="small"
        close={handleCloseModal}
        title={<>Download file with {obGridParams.totalItems} claims?</>}
        modalFooter={
          <FooterStyle>
            <Button variant="secondary" width="100%" size="large" radius="rounded" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="primary"
              width="100%"
              size="large"
              radius="rounded"
              contentType="text-icon"
              onClick={handelDownload}
            >
              <FaDownload /> Yes, download
            </Button>
          </FooterStyle>
        }
      >
        <ModalContent>
          <p>
            This might take a few minutes. Once your file is ready to download it will be available in the Notifications
            menu.
          </p>
        </ModalContent>
      </ObModal>
    </Div>
  )
}

const ModalContent = styled.div`
  p {
    ${({ theme }) => theme.typography.m.normal}
    color: ${({ theme }) => theme.colors.neutral.neutral};
  }
`
const FooterStyle = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: -webkit-fill-available;
`
const Div = styled.div<{ hasFilters: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: 0.5rem;

  button:focus {
    outline: none;
  }

  .display,
  .clear {
    display: flex;
    align-items: center;
    padding: 0.25rem 0.375rem;
    gap: 0.25rem;
    line-height: 1.5rem;
    cursor: default;

    ${({ hasFilters }) =>
      hasFilters &&
      css`
        &:hover {
          cursor: pointer;
        }
      `}
  }

  .display {
    height: 32px;
    margin-right: 4px;
    background: ${({ theme }) => theme.colors.grey.lightest};
    color: ${({ theme }) => theme.colors.grey.dark};
    font-size: 0.875rem;
    font-weight: 600;
    padding-right: 0.75rem;
    border-radius: 0.5rem;
    line-height: 1.14;
    white-space: nowrap;
    margin-left: 0.5rem;
  }

  .clear {
    height: 32px;
    background: ${({ theme }) => theme.colors.primary.neutral};
    font-weight: 600;
    font-size: 0.875rem;
    color: ${({ theme }) => theme.colors.neutral.lightest};
    padding: 0.5rem 0.75rem;
    letter-spacing: normal;
    text-align: center;
    border-radius: 8px;

    ${({ hasFilters }) =>
      hasFilters &&
      css`
        &:hover {
          background: ${({ theme }) => theme.colors.grey.darker}ED;
        }
      `}
  }
  .clear:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
  }
`
